/* Copyright (c) 2020-present CloudIO Inc */

import { IoAudit } from '@cloudio-saas/datasource-types';
import { useStateXAtomValue } from '@cloudio-saas/statex';
import {
  Dialog,
  Toolbar,
  Typography,
  Divider,
  IconButton,
  Theme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DiffEditor, Monaco } from '@monaco-editor/react';
import { formatDistanceToNow } from 'date-fns';
import Icon from 'icons/Icon';
import { PageView } from 'layout/Sidebar/atoms';
import type { editor } from 'monaco-editor';
import { usePlatform } from 'platform/PlatformHooks';
import React, { useState } from 'react';
import { useStoreBusy } from 'store/useAppStore';
import useDataStore from 'store/useDataStore';
import { useTheme } from 'theme/Theme';
import WaveDots from 'utils/WaveDots';
import { selectionAtom } from './PageControllerHistory';
import { usePubSub } from 'common/Context';
import ExpandCollapseIcon from '../ExpandCollapseIcon';
import { isViewOnlyDeveloper } from 'root/useUser';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '2px 4px',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  title: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  iconButton: {
    padding: 10,
  },
  divider: {
    height: 28,
    margin: 4,
  },
}));

export default function PageControllerHistoryHeader({
  setView,
  appUid,
}: {
  appUid: string;
  setView: (_view: PageView) => void;
}): React.ReactElement {
  const store = useDataStore<IoAudit>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoAudit',
    alias: 'IoAudit-diff-alias',
  });
  const busy = useStoreBusy(store);
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState({ original: '', modified: '', title: '' });
  const classes = useStyles();
  const selection = useStateXAtomValue(selectionAtom);
  const theme = useTheme();
  const [navi, setNavi] = useState<editor.IDiffNavigator | null>(null);
  const platform = usePlatform();
  const pubSub = usePubSub();
  const isViewOnlyUser = isViewOnlyDeveloper(platform, appUid);

  function handleClose() {
    setOpen(false);
  }

  function onMount(_editor: editor.IStandaloneDiffEditor, monaco: Monaco) {
    const _navi = monaco.editor.createDiffNavigator(_editor, {
      followsCaret: true,
      ignoreCharChanges: true,
    });
    setNavi(_navi);
  }

  const options: editor.IStandaloneEditorConstructionOptions = {
    folding: true,
    lineNumbers: 'on' as const,
    minimap: { enabled: false },
    readOnly: true,
    scrollBeyondLastLine: false,
    selectOnLineNumbers: true,
  };

  const twoSelection = selection[0] && selection[1];
  const hasSelection = selection[0] || selection[1];

  return (
    <div className={classes.root}>
      <IconButton
        onClick={() => {
          store.reset();
          setView('edit');
        }}
        className={classes.iconButton}
        aria-label="Back"
      >
        <Icon iconName="chevron-left" />
      </IconButton>
      <Typography className={classes.title}>
        Controller Change History
      </Typography>
      {hasSelection ? (
        <Divider className={classes.divider} orientation="vertical" />
      ) : null}
      {!twoSelection && hasSelection ? (
        <IconButton
          disabled={isViewOnlyUser}
          data-tip="Restore Version"
          onClick={async () => {
            const auditId = selection[0] || selection[1];
            if (busy || !auditId) return;
            const r = await store.query({
              props: {
                data: { datasourceUid: 'IoPages', attributeCode: 'controller' },
                filter: [{ auditId: { eq: auditId } }],
                projection: {
                  newClobValue: 1,
                },
                limit: 1,
              },
            });
            if (r) {
              const _rec = store.getCurrentRecord();
              if (_rec) {
                pubSub.pub(
                  'restore-page-controller',
                  {
                    controller: _rec.newClobValue,
                  },
                  true,
                );
                setView('edit');
              }
            }
          }}
        >
          <Icon disabled={isViewOnlyUser} color="primary" iconName="history" />
        </IconButton>
      ) : null}
      {twoSelection ? (
        <IconButton
          disabled={busy}
          color="primary"
          data-tip="View difference between selected versions"
          aria-label="View difference between selected versions"
          onClick={async () => {
            if (busy) return;
            const r = await store.query({
              props: {
                data: { datasourceUid: 'IoPages', attributeCode: 'controller' },
                filter: [
                  {
                    anyOf: [
                      { auditId: { eq: selection[0] ?? -1 } },
                      { auditId: { eq: selection[1] ?? -1 } },
                    ],
                  },
                ],
                projection: {
                  newClobValue: 1,
                  auditId: 1,
                  updatedBy: 1,
                  updateDate: 1,
                },
                limit: 2,
              },
            });
            const records = Object.values(store.records());
            if (r && records.length === 2) {
              let original = '';
              let modified = '';
              let title = '';
              if (records[0].auditId === selection[0]) {
                original = records[0].newClobValue ?? '';
                modified = records[1].newClobValue ?? '';
                title = `${formatDistanceToNow(
                  new Date(records[0].updateDate ?? ''),
                  {
                    addSuffix: true,
                    includeSeconds: true,
                  },
                )} by ${records[0].updatedBy} - ${formatDistanceToNow(
                  new Date(records[1].updateDate ?? ''),
                  {
                    addSuffix: true,
                    includeSeconds: true,
                  },
                )} by ${records[1].updatedBy}`;
              } else {
                original = records[1].newClobValue ?? '';
                modified = records[0].newClobValue ?? '';
                title = `${formatDistanceToNow(
                  new Date(records[1].updateDate ?? ''),
                  {
                    addSuffix: true,
                    includeSeconds: true,
                  },
                )} by ${records[1].updateDate} - ${formatDistanceToNow(
                  new Date(records[0].updateDate ?? ''),
                  {
                    addSuffix: true,
                    includeSeconds: true,
                  },
                )} by ${records[0].updatedBy}`;
              }
              if (original === modified) {
                platform.showInfo('No changes found!');
                return;
              }
              setCode({ original, modified, title });
              setOpen(true);
            }
          }}
        >
          {busy ? <WaveDots active /> : <Icon color="primary" iconName="eye" />}
        </IconButton>
      ) : null}
      <ExpandCollapseIcon />
      <Dialog fullScreen open={open} onClose={handleClose}>
        <Toolbar>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Controller Diff: {code.title}
          </Typography>
          <IconButton
            data-tip="Go To Previous Difference"
            onClick={async () => {
              navi?.previous();
            }}
          >
            <Icon iconName="chevron-left" />
          </IconButton>
          <IconButton
            data-tip="Go To Next Difference"
            onClick={async () => {
              navi?.next();
            }}
          >
            <Icon iconName="chevron-right" />
          </IconButton>
          <IconButton onClick={handleClose}>
            <span aria-hidden="false" style={{ display: 'none' }}>
              Close
            </span>
            <Icon iconName="times" />
          </IconButton>
        </Toolbar>
        <DiffEditor
          height="100%"
          theme={theme.palette.mode === 'light' ? 'vs' : 'vs-dark'}
          original={code.original}
          modified={code.modified}
          language="javascript"
          options={options}
          onMount={onMount}
        />
      </Dialog>
    </div>
  );
}
